import { resolveRpLink, type RpSectionOf } from '@mlm/shared';
import { cn } from '../../lib/cn';
import { useRpCtx } from '../context';
import { RpSectionHeader } from '../primitives';
import { RP_BTN_BASE, RP_BTN_SIZE, RP_BTN_VARIANT, rpCols } from '../tokens';

/**
 * Kit cards for this campaign.
 *
 * The ONE section that points at data rather than holding it: the products come
 * from the page's own `kit_product_ids` tagging (the Packages card below the
 * editor), resolved server-side into `ctx.packages`. So the campaign's package
 * list has one home, and adding a kit to a campaign doesn't mean re-editing the
 * page's copy.
 *
 * Each card's button always routes to sign-up with that kit preselected, so a
 * card can never be published pointing somewhere that drops sponsor attribution.
 */
export function RpPackages({ section }: { section: RpSectionOf<'packages'> }) {
  const ctx = useRpCtx();
  const c = section.config;
  const packages = ctx.packages ?? [];

  if (!packages.length) {
    // In the builder, say why it's empty — otherwise an admin adds the section,
    // sees nothing, and concludes it's broken. On the live page, render nothing.
    return ctx.mode === 'preview' ? (
      <p className="rounded-md border border-dashed p-6 text-center text-sm opacity-70">
        No packages tagged yet. Tick the activation kits for this campaign in the Packages card
        below, and they will appear here.
      </p>
    ) : null;
  }

  return (
    <div className="space-y-8">
      <RpSectionHeader heading={c.heading} subheading={c.subheading} />
      <div className={cn('grid gap-6', rpCols(c.cols ?? 3))}>
        {packages.map((p) => (
          <div
            key={p.productId}
            className="flex flex-col gap-3 rounded-lg bg-card p-6 text-left text-card-foreground shadow-card"
          >
            {p.imageUrl ? (
              <img src={p.imageUrl} alt="" className="h-32 w-full rounded object-cover" />
            ) : null}
            <h3 className="font-heading text-base font-semibold">{p.name}</h3>
            {c.showPrice !== false && p.price ? (
              <p className="font-heading text-2xl font-bold text-primary">{p.price}</p>
            ) : null}
            {c.showPv && p.pv ? <p className="text-xs opacity-70">{p.pv} PV</p> : null}
            <a
              href={resolveRpLink({ kind: 'kit', productId: p.productId }, ctx.link)}
              className={cn('mt-auto', RP_BTN_BASE, RP_BTN_VARIANT.primary, RP_BTN_SIZE.md)}
            >
              {c.ctaLabel || 'Get started'}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
